import type { Me } from "../types";

// Thin client-side reads of the RBAC principal the server resolves and returns on
// GET /api/me (orgs / teams / roles / permissions). The API is the authority and
// still enforces every action; these helpers only decide whether to SHOW an
// affordance and how to display the caller's access. We deliberately do NOT
// duplicate the shared permission matrix here — `me.permissions` is already the
// server's flat, resolved capability list, so a membership check is enough.

// An app admin (or the open local gate) collapses to "everything", matching the
// server's `admin` short-circuit — so a check never depends on `permissions`
// being enumerated for admins.
export function hasPermission(me: Me | null, action: string): boolean {
  if (!me) return false;
  if (me.admin === true) return true;
  return me.permissions?.includes(action) === true;
}

// Every role the user holds, newest model first. Falls back to the single primary
// `role` (older /api/me payloads) and finally to the app default so the UI always
// has something to show for a signed-in user.
export function heldRoles(me: Me | null): string[] {
  if (!me) return [];
  if (me.roles && me.roles.length) return me.roles;
  if (me.role) return [me.role];
  return ["reader"];
}

// Teams the user belongs to (SME scope / collective departments), primary `team`
// folded in for older payloads.
export function heldTeams(me: Me | null): string[] {
  if (!me) return [];
  const teams = new Set(me.teams ?? []);
  if (me.team) teams.add(me.team);
  return [...teams];
}

// Orgs the user may act within. An admin's list is intentionally empty on the wire
// (all-orgs via the admin flag) — callers render that as "All organizations".
export function heldOrgs(me: Me | null): string[] {
  return me?.orgs ?? [];
}
